import { useEffect, useState } from 'react'
import { peekMediaUrl, resolveMediaUrl } from '@/services/media/urls'

/**
 * Resolves a signed URL for a photograph.
 *
 * Components never hold URLs of their own: they hold a media id and ask for a
 * link when they need to draw. A link that has expired, or that belongs to a
 * relationship that has since been cut, simply stops resolving. (§79, §117)
 */
export function useMediaUrl(
  mediaId: string | null | undefined,
  variant: 'full' | 'thumb' = 'thumb',
): string | null {
  const [url, setUrl] = useState<string | null>(() =>
    mediaId ? peekMediaUrl(mediaId, variant) : null,
  )

  useEffect(() => {
    if (!mediaId) {
      setUrl(null)
      return
    }
    const cached = peekMediaUrl(mediaId, variant)
    if (cached) {
      setUrl(cached)
      return
    }

    let alive = true
    setUrl(null)
    resolveMediaUrl(mediaId, variant)
      .then((resolved) => {
        if (alive) setUrl(resolved)
      })
      .catch(() => {
        if (alive) setUrl(null)
      })
    return () => {
      alive = false
    }
  }, [mediaId, variant])

  return url
}

/** Resolves several at once, for a gallery or a rail. */
export function useMediaUrls(
  mediaIds: Array<string | null | undefined>,
  variant: 'full' | 'thumb' = 'thumb',
): Record<string, string> {
  const [urls, setUrls] = useState<Record<string, string>>({})
  const key = mediaIds.filter(Boolean).join(',')

  useEffect(() => {
    let alive = true
    const ids = key ? key.split(',') : []
    if (ids.length === 0) {
      setUrls({})
      return
    }

    Promise.all(
      ids.map(async (id) => {
        try {
          return [id, await resolveMediaUrl(id, variant)] as const
        } catch {
          return null
        }
      }),
    ).then((entries) => {
      if (!alive) return
      const next: Record<string, string> = {}
      for (const entry of entries) if (entry) next[entry[0]] = entry[1]
      setUrls(next)
    })

    return () => {
      alive = false
    }
  }, [key, variant])

  return urls
}
